import { DEV } from '../dev';

/**
 * Input devices merged into one analog sample per simulation tick. Steering
 * smoothing lives in the simulation (the steering rack), so every device gets
 * the same deterministic response and replays stay exact.
 */
export interface RawInput {
  throttle: number;
  brake: number;
  /** -1..1, positive = left */
  steer: number;
}

/** 'confirm' and 'back' come from a controller's A and B: the poster's big button, and back / skip. */
export type Action = 'restart' | 'devPanel' | 'mute' | 'pause' | 'start' | 'confirm' | 'back' | 'challenge';
export type Device = 'keyboard' | 'gamepad' | 'touch';

export interface InputSource {
  sample(out: RawInput): void;
}

const DEAD_ZONE = 0.12;

export class KeyboardInput implements InputSource {
  private down = new Set<string>();
  constructor(onAction: (a: Action) => void, onUse: () => void) {
    window.addEventListener('keydown', (e) => {
      // The tuning panel's keys, only where it exists (never on the live game: there F1 and ` are left alone).
      if (DEV && (e.code === 'F1' || e.code === 'Backquote')) {
        e.preventDefault();
        if (!e.repeat) onAction('devPanel');
        return;
      }
      if (isTypingTarget(e.target)) return;
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space'].includes(e.code)) e.preventDefault();
      if (!e.repeat) {
        if (e.code === 'KeyR') onAction('restart');
        else if (e.code === 'KeyM') onAction('mute');
        else if (e.code === 'KeyC') onAction('challenge');
        else if (e.code === 'Escape' || e.code === 'KeyP') onAction('pause');
        else onAction('start');
      }
      this.down.add(e.code);
      onUse();
    });
    window.addEventListener('keyup', (e) => this.down.delete(e.code));
    window.addEventListener('blur', () => this.down.clear());
  }

  sample(out: RawInput): void {
    const d = this.down;
    if (d.has('KeyW') || d.has('ArrowUp')) out.throttle = 1;
    if (d.has('KeyS') || d.has('ArrowDown') || d.has('Space')) out.brake = 1;
    let s = 0;
    if (d.has('KeyA') || d.has('ArrowLeft')) s += 1;
    if (d.has('KeyD') || d.has('ArrowRight')) s -= 1;
    out.steer += s;
  }
}

function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
}

export class GamepadInput implements InputSource {
  private prevButtons: boolean[] = [];
  constructor(private onAction: (a: Action) => void, private onUse: () => void) {}

  sample(out: RawInput): void {
    const pads = navigator.getGamepads ? navigator.getGamepads() : [];
    for (const gp of pads) {
      if (!gp || !gp.connected) continue;
      const ax = gp.axes[0] ?? 0;
      const mag = Math.abs(ax);
      const steer = mag < DEAD_ZONE ? 0 : Math.sign(ax) * Math.pow((mag - DEAD_ZONE) / (1 - DEAD_ZONE), 1.4);
      const rt = gp.buttons[7]?.value ?? 0;
      const lt = gp.buttons[6]?.value ?? 0;
      const a = gp.buttons[0]?.pressed ? 1 : 0;
      const x = gp.buttons[2]?.pressed ? 1 : 0;
      out.steer -= steer; // stick right = steer right = negative
      out.throttle = Math.max(out.throttle, rt, a);
      out.brake = Math.max(out.brake, lt, x);
      if (steer !== 0 || rt > 0.05 || lt > 0.05 || a || x) this.onUse();
      const pressed = gp.buttons.map((b) => b.pressed);
      const edge = (i: number): boolean => !!pressed[i] && !this.prevButtons[i];
      if (edge(3) || edge(8)) this.onAction('restart'); // Y / Back
      if (edge(9)) this.onAction('pause'); // Start
      if (edge(0)) this.onAction('confirm'); // A: gas while racing, the big button on the poster
      if (edge(1)) this.onAction('back'); // B
      if (edge(7)) this.onAction('start'); // RT
      this.prevButtons = pressed;
      return; // first connected pad only
    }
  }
}

export class InputManager {
  readonly keyboard: KeyboardInput;
  readonly gamepad: GamepadInput;
  private sources: InputSource[] = [];
  lastDevice: Device = 'keyboard';
  private actionListeners: ((a: Action) => void)[] = [];

  constructor() {
    const fire = (a: Action): void => {
      for (const fn of this.actionListeners) fn(a);
    };
    this.keyboard = new KeyboardInput(fire, () => (this.lastDevice = 'keyboard'));
    this.gamepad = new GamepadInput(fire, () => (this.lastDevice = 'gamepad'));
    this.sources.push(this.keyboard, this.gamepad);
    this.fire = fire;
  }

  /** Lets on-screen controls raise the same actions. */
  readonly fire: (a: Action) => void;

  addSource(src: InputSource): void {
    this.sources.push(src);
  }

  onAction(fn: (a: Action) => void): void {
    this.actionListeners.push(fn);
  }

  sample(): RawInput {
    const out: RawInput = { throttle: 0, brake: 0, steer: 0 };
    for (const s of this.sources) s.sample(out);
    out.steer = Math.max(-1, Math.min(1, out.steer));
    return out;
  }
}
